import { useNode } from "@craftjs/core";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import useApplyGlobalSettings, { CODEBLOCK_TYPES } from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { toggleSegmentSettings } from "../../../../redux/actions/segmentSettings";
import { updateDividerStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import {
  isValidSuggestedColor,
  sliderToOpacity,
} from "../../../../utils/helpers";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import ColorPopup from "../../Shared/ColorPopup";
import DividerStyles from "../../Shared/DividerStyles";
import MarginBox from "../../Shared/MarginBox";
import SettingsSlider from "../../Shared/SettingsSlider";

export const Divider = ({
  width,
  color,
  style,
  isApplyMarginOnAllSides,
  margin,
  allSideMargin,
  // Segments saved before the opacity control shipped have no opacity prop
  // on their Divider nodes; without a default, `1 - opacity` below renders
  // an invalid `NaN` opacity until the reducer-sync effect backfills it.
  opacity = 0,
}) => {
  const {
    connectors: { connect, drag },
    actions: { setProp },
    id
  } = useNode();

  const { getPaletteColor } = usePaletteColor();
  const { divider: dividerProps } = useSelector((state) => state.codeblockStyles);

  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.Divider);

  // Init with divider props for new components
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        // Only apply reducer values if component props are null or undefined
        // This ensures we don't override manually set values
        if (props.width === undefined) {
          props.width = dividerProps.width;
        }
        if (props.color === undefined) {
          props.color = dividerProps.color || "suggested_color_0";
        }
        if (props.style === undefined) {
          props.style = dividerProps.style;
        }
        if (props.margin === undefined) {
          props.margin = { ...dividerProps.margin };
        }
        if (props.isApplyMarginOnAllSides === undefined) {
          props.isApplyMarginOnAllSides = dividerProps.isApplyMarginOnAllSides;
        }
        if (props.allSideMargin === undefined) {
          props.allSideMargin = dividerProps.allSideMargin;
        }
        if (props.opacity === undefined) {
          props.opacity = dividerProps.opacity;
        }
      });
    }, 50);

    return () => clearTimeout(timeoutId);
  }, []);

  // Set default suggested color if none is provided
  useEffect(() => {
    if (!color) {
      const timeoutId = setTimeout(() => {
        setProp((props) => {
          props.color = "suggested_color_0";
        });
      }, 10);

      return () => clearTimeout(timeoutId);
    }
  }, [color, setProp]);

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  const isGradient =
    color?.includes("linear-gradient") || color?.includes("radial-gradient");

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <div
          style={{
            display: "flex",
            flex: 1,
            cursor: "pointer",
            padding: marginStyle,
          }}
        >
          <div
            style={{
              width: "100%",
              borderWidth: 0,
              borderTopWidth: width,
              borderStyle: style,
              borderTopColor: isGradient
                ? "transparent"
                : isValidSuggestedColor(color)
                ? getPaletteColor(color)
                : color,
              borderImage: isGradient ? `${color} 1` : "none",
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              opacity: 1 - opacity,
            }}
          />
        </div>
      </BlockIndicator>
    </div>
  );
};

const DividerSettings = () => {
  const {
    actions: { setProp },
    width,
    color,
    style,
    isApplyMarginOnAllSides,
    margin,
    allSideMargin,
    opacity,
  } = useNode((node) => ({
    width: node.data.props.width,
    color: node.data.props.color,
    style: node.data.props.style,
    isApplyMarginOnAllSides: node.data.props.isApplyMarginOnAllSides,
    margin: node.data.props.margin,
    allSideMargin: node.data.props.allSideMargin,
    opacity: node.data.props.opacity,
  }));

  const handleAdjustment = (side, operation) => {
    const newValue =
      operation === "increase" ? margin[side] + 1 : margin[side] - 1;
    if (newValue >= 0) handleChange("margin", newValue, side);
  };
  const dispatch = useDispatch();

  const gradientRegex = /(linear|radial)-gradient\(([^)]+)\)/;

  const handleChange = (type, value, side) => {
    dispatch(toggleSegmentSettings(false));
    setProp((props) => {
      switch (type) {
        case "width":
          props.width = value;
          break;
        case "margin":
          if (value >= 0 && value <= 100) {
            props.margin[side] = value;
          }
          break;
        case "allSideMargin":
          props.allSideMargin = value;
          break;
        default:
        case "marginCheckbox":
          props.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;
          break;
        case "style":
          props.style = value;
          break;
        case "color":
          props.color = value;
          if (gradientRegex.test(value)) props.style = "solid";
          break;
        case "resetColor":
          props.color = "#000000";
          break;
        case "opacity":
          props.opacity = sliderToOpacity(value);
          break;
      }
      
      // Create a new divider props object to update the reducer
      const updatedDividerProps = {
        width: props.width,
        color: props.color,
        style: props.style,
        margin: { ...props.margin },
        isApplyMarginOnAllSides: props.isApplyMarginOnAllSides,
        allSideMargin: props.allSideMargin,
        opacity: props.opacity,
      };
      
      // Update the divider reducer
      dispatch(updateDividerStyles(updatedDividerProps));
    }, 500);
  };

  return (
    <>
      <DividerStyles
        selectedStyle={style}
        setSelectedStyle={(style) => handleChange("style", style)}
      />

      <SettingsSlider
        title="Thickness"
        sliderValue={width}
        onValueChange={(val) => handleChange("width", val)}
        min={1}
      />
      <MarginBox
        margin={margin}
        increaseMargin={(side) => handleAdjustment(side, "increase")}
        decreaseMargin={(side) => handleAdjustment(side, "decrease")}
        isApplyMarginOnAllSides={isApplyMarginOnAllSides}
        allSideMargin={allSideMargin}
        handleMarginCheckBox={() => handleChange("marginCheckbox")}
        handleSliderMarginChange={(value) =>
          handleChange("allSideMargin", value)
        }
        handleMarginChange={(side, newValue) =>
          handleChange("margin", newValue, side)
        }
      />
      <ColorPopup
        title="Color"
        background={color}
        handleChangeBackgroundColor={(val) => handleChange("color", val)}
        resetBackgroundColor={() => handleChange("resetColor")}
        sliderValue={opacity * 100}
        sliderChange={(val) => handleChange("opacity", val)}
        sliderBoxTitle={"Transparency"}
        showSuggestedColorPalette={true}
        isHideGradient={true}
      />
    </>
  );
};

export const DividerDefaultProps = {
  width: undefined, // Will get from reducer
  color: undefined, // Color will be set based on selectedSegmentColor or reducer
  style: undefined, // Will get from reducer
  margin: undefined, // Will get from reducer
  isApplyMarginOnAllSides: undefined, // Will get from reducer
  allSideMargin: undefined, // Will get from reducer
  opacity: undefined, // Will get from reducer
};

Divider.craft = {
  props: DividerDefaultProps,
  related: {
    settings: DividerSettings,
  },
  displayName: 'DividerWidget',
};
